import { Box } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import customerApis from "src/api/customer"
import { OtpForm } from "src/components"
import CommonContainer from "src/components/CommonContainer"
import {
  useAuthContext,
  useDialog,
  useGetAuthenId,
  useToastify,
} from "src/hooks"
import { theme } from "src/providers/ThemeProvider"
import { TextField } from "src/shared"
import Button from "src/shared/Button"
import { ValidateRequestOtpModel } from "src/types"
import { setWithExpiry } from "src/utils/localStorages"

const regex = /^[0-9\b]+$/

const CustomerInfo = () => {
  const { onConfirmPhoneNumber, isProfileLoading, setHasAuthenticated } =
    useAuthContext()
  const [formError, setFormError] = useState({
    phoneNumber: false,
    otpCode: false,
  })

  const { showError } = useToastify()

  const { onOpen: onOtpOpen, onClose: onOtpClose, open: openOtp } = useDialog()

  const [phoneNumber, setPhoneNumber] = useState("")
  const [otpCode, setOptCode] = useState("")

  const { onGetAuthenId, authenIdData } = useGetAuthenId()

  const onSetOtpError = useCallback((isError = false) => {
    setFormError(prev => ({ ...prev, otpCode: isError }))
  }, [])

  const handleOtpClose = useCallback(async () => {
    onSetOtpError()
    onOtpClose()
  }, [onOtpClose, onSetOtpError])

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: ValidateRequestOtpModel) =>
      customerApis.validatOpt(payload),
    onSuccess: d => {
      if (d.ErrorCode === 0) {
        handleOtpClose()
        setHasAuthenticated(true)
        setWithExpiry("phoneNumber", phoneNumber, 24 * 60 * 60 * 1000)
      } else {
        onSetOtpError(true)
      }
    },
    onError: () => {
      onSetOtpError(true)
    },
  })

  const handleOtpChange = (otpVal?: string) => {
    setOptCode(otpVal || "")
    onSetOtpError(false)
  }

  const onSetPhoneNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e || e.target?.value === "-") return
      if (e?.target.value === "" || regex.test(e?.target.value || "")) {
        setPhoneNumber(e.target?.value)
        setFormError({
          otpCode: false,
          phoneNumber: false,
        })
      }
      return
    },
    [setPhoneNumber]
  )

  const onShowOtpForm = useCallback(async () => {
    if (phoneNumber) {
      const response = await onConfirmPhoneNumber(phoneNumber)
      if (!response) {
        setFormError(prev => ({ ...prev, phoneNumber: true }))
      } else {
        await onGetAuthenId(phoneNumber)
          .then(x => {
            if (x.ErrorCode === 1) {
              showError("Vui lòng chờ 5 phút để lấy mã OTP mới!")
              return
            }

            onOtpOpen()
          })
          .catch(() => {
            showError("Đã xảy ra lỗi, vui lòng thử lại sau!")
          })
      }
    }
  }, [
    onOtpOpen,
    onGetAuthenId,
    showError,
    phoneNumber,
    setFormError,
    onConfirmPhoneNumber,
  ])

  const verifyOtp = useCallback(() => {
    mutate({
      otpId: authenIdData?.OtpId || "",
      otpCode: otpCode || "",
    })
  }, [mutate, authenIdData, otpCode])

  return (
    <Box
      padding={theme.spacing(0, 2)}
      width="100%"
      display="flex"
      flexDirection="column"
      position="relative"
      alignItems="center"
    >
      <CommonContainer
        bannerProps={{
          alt: "customer_info",
          src: "/assets/images/user_info_banner.png",
        }}
      >
        <Box
          width="100%"
          textAlign="center"
          sx={{
            paddingTop: theme.spacing(3),
            [theme.breakpoints.down(600)]: {
              "& > img": {
                width: "50%",
              },
              paddingTop: theme.spacing(1),
            },
          }}
        >
          <img src="/assets/images/doi_qua_hap_dan.png" alt="doi_qua_hap_dan" />
        </Box>
        <TextField
          value={phoneNumber}
          placeholder="Số điện thoại"
          onChange={onSetPhoneNumber}
          sx={{
            margin: theme.spacing(4, 0, 2.5, 0),
            [theme.breakpoints.down(600)]: {
              width: "80%",
            },
            width: 394,
          }}
          error={formError.phoneNumber}
          helperText={
            !!formError.phoneNumber
              ? "Số điện thoại chưa tham gia chương trình!"
              : ""
          }
        />
        <Button
          disabled={!phoneNumber || isProfileLoading}
          onClick={onShowOtpForm}
          loading={isProfileLoading}
        >
          Xác nhận
        </Button>
      </CommonContainer>
      <OtpForm
        onOtpChange={handleOtpChange}
        onSubmit={verifyOtp}
        open={openOtp}
        onClose={handleOtpClose}
        isLoading={isPending}
        error={formError.otpCode}
      />
    </Box>
  )
}

export default CustomerInfo
